import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useToast } from "@/hooks/use-toast";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { associationApi } from '@/services/api';
import { managementCompanyApi } from '@/services/api/organizationApi';
import { documentGroupApi } from '@/services/api/documentApi';
import { pricingTableApi } from '@/services/api/pricingApi';
import { financialAccountApi } from '@/services/api/financialAccountApi';
import { AssociationType, TaxClassification } from '@/models/Association';
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Building, Loader2, ExternalLink } from "lucide-react";
import { UnderlinedTabs, UnderlinedTabsContent, UnderlinedTabsList, UnderlinedTabsTrigger } from "@/components/ui/underlined-tabs";
import { US_STATES } from '@/utils/statesList';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import LocationPickerButton from '@/components/ui/location-picker-button';
import { DocumentGroupList } from "@/components/associations/DocumentGroupList";
import { DocumentGroupSelector } from "@/components/associations/DocumentGroupSelector";

const phoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
const einRegex = /^\d{2}-\d{7}$/;
const zipCodeRegex = /^\d{5}(-\d{4})?$/;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Association name must be at least 2 characters.",
  }),
  association_type: z.enum(["Condominium", "HOA", "COOP"] as const),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }).regex(emailRegex, {
    message: "Email format is invalid. It should be like example@domain.com"
  }).optional().or(z.literal('')),
  phone: z.string().regex(phoneRegex, {
    message: "Please enter a valid phone number (e.g. 555-123-4567 or (555) 123-4567).",
  }).optional().or(z.literal('')),
  ein: z.string().regex(einRegex, {
    message: "Please enter a valid EIN in the format XX-XXXXXXX.",
  }).optional().or(z.literal('')),
  tax_classification: z.enum([
    "LLC – Partnership",
    "LLC – C Corporation",
    "LLC – S Corporation",
    "C Corporation",
    "S Corporation", 
    "Individual or Single LLC",
    "Partnership"
  ] as const).optional(),
  billing_address: z.string().min(1, {
    message: "Billing address is required.",
  }).optional().or(z.literal('')),
  billing_unit: z.string().optional().or(z.literal('')),
  billing_city: z.string().min(1, {
    message: "City is required.",
  }).optional().or(z.literal('')),
  billing_state: z.string().length(2, {
    message: "Please select a state.",
  }).optional().or(z.literal('')),
  billing_zip: z.string().regex(zipCodeRegex, {
    message: "Please enter a valid ZIP code (e.g. 12345 or 12345-6789).",
  }).optional().or(z.literal('')),
  default_property_address: z.string().min(1, {
    message: "Property address is required.",
  }).optional().or(z.literal('')),
  default_property_city: z.string().min(1, {
    message: "City is required.",
  }).optional().or(z.literal('')),
  default_property_state: z.string().length(2, {
    message: "Please select a state.",
  }).optional().or(z.literal('')),
  default_property_zip: z.string().regex(zipCodeRegex, {
    message: "Please enter a valid ZIP code (e.g. 12345 or 12345-6789).",
  }).optional().or(z.literal('')),
  default_property_latitude: z.number().optional(),
  default_property_longitude: z.number().optional(),
  account_holder_name: z.string().optional().or(z.literal('')),
  bank_name: z.string().optional().or(z.literal('')),
  account_number: z.string().optional().or(z.literal('')),
  routing_number: z.string().optional().or(z.literal('')),
  account_type: z.string().optional().or(z.literal('')),
  street_address: z.string().optional().or(z.literal('')),
  payout_city: z.string().optional().or(z.literal('')),
  state_province: z.string().optional().or(z.literal('')),
  postcode: z.string().optional().or(z.literal('')),
  pricing_table_id: z.number().optional(),
  document_groups: z.array(z.number()).optional(),
  external_id: z.string().optional(),
  accepts_external_applicants: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const AssociationNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDocumentGroups, setSelectedDocumentGroups] = useState<number[]>([]);
  const [defaultManagementCompanyId] = useState<number>(1);
  const [activeTab, setActiveTab] = useState("general-info");
  const [formInitialized, setFormInitialized] = useState(false);
  const [useSameBillingAddress, setUseSameBillingAddress] = useState(true);

  const initialData = location.state?.initialData;

  const managementCompanyId = user?.management_company_id || defaultManagementCompanyId;
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialData || {
      name: '',
      association_type: undefined,
      email: '',
      phone: '',
      ein: '',
      tax_classification: undefined,
      billing_address: '',
      billing_unit: '',
      billing_city: '',
      billing_state: '',
      billing_zip: '',
      default_property_address: '',
      default_property_city: '',
      default_property_state: '',
      default_property_zip: '',
      default_property_latitude: undefined,
      default_property_longitude: undefined,
      account_holder_name: '',
      bank_name: '',
      account_number: '',
      routing_number: '',
      account_type: '',
      street_address: '',
      payout_city: '',
      state_province: '',
      postcode: '',
      pricing_table_id: undefined,
      document_groups: [],
      external_id: '',
      accepts_external_applicants: false,
    },
  });

  const { data: managementCompany, isLoading: managementCompanyLoading } = useQuery({
    queryKey: ['managementCompany', managementCompanyId],
    queryFn: () => {
      console.log("Fetching management company data for ID:", managementCompanyId);
      return managementCompanyApi.getById(managementCompanyId);
    },
    enabled: !!managementCompanyId,
  });

  const { data: pricingTables, isLoading: pricingTableLoading } = useQuery({
    queryKey: ['pricing-tables', { management_company_id: currentUser?.management_company_id }],
    queryFn: () => {
      if (!currentUser?.management_company_id) {
        throw new Error('Management company ID is required');
      }
      return pricingTableApi.list({ 
        management_company_id: currentUser.management_company_id 
      });
    },
    enabled: !!currentUser?.management_company_id
  });

  const { data: documentGroups, isLoading: documentGroupsLoading } = useQuery({
    queryKey: ['documentGroups'],
    queryFn: () => documentGroupApi.list(),
  });

  useEffect(() => {
    if (formInitialized || managementCompanyLoading) return;
    
    if (managementCompany) {
      console.log("Management company loaded:", managementCompany);
      
      if (!initialData) {
        if (managementCompany.default_pricing_table_id) {
          console.log("Setting default pricing table ID:", managementCompany.default_pricing_table_id);
          form.setValue('pricing_table_id', managementCompany.default_pricing_table_id);
        }
        
        if (managementCompany.default_document_groups?.length) {
          console.log("Setting default document groups:", managementCompany.default_document_groups);
          setSelectedDocumentGroups(managementCompany.default_document_groups);
          form.setValue('document_groups', managementCompany.default_document_groups);
        }
      } else {
        if (initialData.document_groups?.length) {
          setSelectedDocumentGroups(initialData.document_groups);
        }
      }
      
      setFormInitialized(true);
    }
  }, [managementCompany, initialData, managementCompanyLoading, form, formInitialized]);

  // Sync payout address with billing address when checkbox is checked
  useEffect(() => {
    if (useSameBillingAddress) {
      const subscription = form.watch((value, { name }) => {
        if (name === 'billing_address' || name === 'billing_city' ||
            name === 'billing_state' || name === 'billing_zip') {
          form.setValue("street_address", value.billing_address || "");
          form.setValue("payout_city", value.billing_city || "");
          form.setValue("state_province", value.billing_state || "");
          form.setValue("postcode", value.billing_zip || "");
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [useSameBillingAddress, form]);

  const handleSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      console.log("Form submitted with data:", data);

      const associationData = {
        name: data.name,
        association_type: data.association_type as AssociationType,
        email: data.email || null,
        phone: data.phone || null,
        ein: data.ein || null,
        tax_classification: data.tax_classification as TaxClassification,
        billing_address: data.billing_address || null,
        billing_unit: data.billing_unit || null,
        billing_city: data.billing_city || null,
        billing_state: data.billing_state || null,
        billing_zip: data.billing_zip || null,
        default_property_address: data.default_property_address || null,
        default_property_city: data.default_property_city || null,
        default_property_state: data.default_property_state || null,
        default_property_zip: data.default_property_zip || null,
        default_property_latitude: data.default_property_latitude || null,
        default_property_longitude: data.default_property_longitude || null,
        management_company_id: managementCompanyId,
        document_groups: selectedDocumentGroups,
        parent_association_id: null,
        pricing_table_id: data.pricing_table_id || null,
        external_id: data.external_id,
        accepts_external_applicants: data.accepts_external_applicants === true,
      };

      console.log("Sending associationData to API:", associationData);
      const response = await associationApi.create(associationData);
      console.log("API response:", response);

      // Save payout account information if any financial fields are provided
      const hasFinancialData = data.account_holder_name || data.bank_name ||
                               data.account_number || data.routing_number;

      if (hasFinancialData && response.id) {
        try {
          // Fetch the financial account for this association
          const accounts = await financialAccountApi.getAccounts({
            account_type: 'association',
            include_balances: false
          });

          const associationAccount = accounts.find((account: any) =>
            account.reference === response.id.toString()
          );

          if (associationAccount) {
            const payoutData = {
              payout_method: 'bank_account' as const,
              account_holder_name: data.account_holder_name || response.name,
              bank_name: data.bank_name || '',
              bank_account_number: data.account_number || '',
              bank_routing_number: data.routing_number || '',
              bank_account_type: data.account_type || 'checking',
              street_address: data.street_address || '',
              city: data.payout_city || '',
              state_province: data.state_province || '',
              postcode: data.postcode || '',
              is_default: true,
            };

            await financialAccountApi.createPayoutAccount(associationAccount.id, payoutData);
          }
        } catch (payoutError) {
          console.error('Error saving payout account:', payoutError);
          // Don't fail the entire operation if payout account save fails
          toast({
            variant: 'destructive',
            title: 'Warning',
            description: 'Association created but financial information could not be saved',
          });
        }
      }

      toast({
        title: 'Success',
        description: 'Association created successfully',
      });

      navigate('/backoffice/associations');
    } catch (error) {
      console.error('Error creating association:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create association',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddressSelect = (addressData: {
    fullAddress: string;
    city?: string;
    state?: string;
    zipCode?: string;
    lat?: number;
    lon?: number;
  }) => {
    console.log("Address selected for property:", addressData);
    
    form.setValue('default_property_address', addressData.fullAddress);
    
    if (addressData.city) {
      form.setValue('default_property_city', addressData.city);
    }
    
    if (addressData.state) {
      form.setValue('default_property_state', addressData.state);
    }
    
    if (addressData.zipCode) {
      form.setValue('default_property_zip', addressData.zipCode);
    }
    
    if (addressData.lat) {
      form.setValue('default_property_latitude', addressData.lat);
    }
    
    if (addressData.lon) {
      form.setValue('default_property_longitude', addressData.lon);
    }
  };

  const handleBillingAddressSelect = (addressData: {
    fullAddress: string;
    city?: string;
    state?: string;
    zipCode?: string;
    lat?: number;
    lon?: number;
  }) => {
    console.log("Address selected for billing:", addressData);
    
    form.setValue('billing_address', addressData.fullAddress);
    
    if (addressData.city) {
      console.log("Setting billing city to:", addressData.city);
      form.setValue('billing_city', addressData.city);
    }
    
    if (addressData.state) {
      form.setValue('billing_state', addressData.state);
    }
    
    if (addressData.zipCode) {
      form.setValue('billing_zip', addressData.zipCode);
    }
  };

  const handleMapLocationSelect = (latitude: number, longitude: number) => {
    console.log("Selected location from map:", { latitude, longitude });
    form.setValue('default_property_latitude', latitude);
    form.setValue('default_property_longitude', longitude);
  };

  const getGoogleMapsUrl = (lat?: number, lng?: number) => {
    if (!lat || !lng) return null;
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };

  const renderPricingTableOptions = () => {
    if (pricingTableLoading) return <SelectItem value="loading">Loading pricing tables...</SelectItem>;
    if (!pricingTables || pricingTables.length === 0) return <SelectItem value="none">No pricing tables available</SelectItem>;
    
    return pricingTables.map((table) => (
      <SelectItem key={table.id} value={table.id.toString()}>
        {table.name}
      </SelectItem>
    ));
  };

  const renderDocumentGroups = () => {
    if (documentGroupsLoading) return <p>Loading document groups...</p>;
    
    return (
      <div className="space-y-4">
        <DocumentGroupSelector
          selectedGroups={selectedDocumentGroups}
          onSelectGroup={(group) => {
            if (!selectedDocumentGroups.includes(group.id)) {
              setSelectedDocumentGroups([...selectedDocumentGroups, group.id]);
              const currentGroups = form.getValues("document_groups") || [];
              form.setValue("document_groups", [...currentGroups, group.id]);
            }
          }}
          onRemoveGroup={(groupId) => {
            setSelectedDocumentGroups(selectedDocumentGroups.filter(id => id !== groupId));
            const currentGroups = form.getValues("document_groups") || [];
            form.setValue("document_groups", currentGroups.filter(id => id !== groupId));
          }}
        />
      </div>
    );
  };

  const onError = (errors: any) => {
    console.log("Form validation errors:", errors);
    
    if (errors.name || errors.association_type || errors.email || 
        errors.phone || errors.ein || errors.tax_classification || 
        errors.pricing_table_id) {
      setActiveTab("general-info");
    } else if (errors.billing_address || errors.billing_unit || 
               errors.billing_city || errors.billing_state || 
               errors.billing_zip || errors.finance_info || 
               errors.bank_account_info) {
      setActiveTab("billing");
    } else if (errors.default_property_address || errors.default_property_city || 
               errors.default_property_state || errors.default_property_zip ||
               errors.default_property_latitude || errors.default_property_longitude) {
      setActiveTab("property-address");
    }
    
    toast({
      variant: "destructive",
      title: "Validation Error",
      description: "Please check the form for errors"
    });
  };

  if (managementCompanyLoading || pricingTableLoading || documentGroupsLoading) {
    return (
      <BackofficeLayout>
        <div className="container mx-auto py-6 flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Loading data...</span>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold flex items-center">
            <Building className="mr-2 h-8 w-8" />
            Create New Association
          </h1>
          <Button variant="outline" onClick={() => navigate('/backoffice/associations')}>
            Back to Associations
          </Button>
        </div>

        <Card>
          <CardContent className="p-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit, onError)} className="space-y-4">
                <UnderlinedTabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <UnderlinedTabsList className="mb-4">
                    <UnderlinedTabsTrigger value="general-info">General Information</UnderlinedTabsTrigger>
                    <UnderlinedTabsTrigger value="billing">Billing</UnderlinedTabsTrigger>
                    <UnderlinedTabsTrigger value="property-address">Default Property Address</UnderlinedTabsTrigger>
                  </UnderlinedTabsList>

                  <UnderlinedTabsContent value="general-info" className="space-y-4">
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Association Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="Association Name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="association_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Association Type *</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                console.log("Association type selected:", value);
                                field.onChange(value);
                              }}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an association type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Condominium">Condominium</SelectItem>
                                <SelectItem value="HOA">HOA</SelectItem>
                                <SelectItem value="COOP">Cooperative</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="external_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>External ID</FormLabel>
                            <FormControl>
                              <Input placeholder="External ID" {...field} />
                            </FormControl>
                            <FormDescription>
                              Optional unique identifier within your management company (will use the association ID if left empty)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input placeholder="Email" type="email" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone</FormLabel>
                            <FormControl>
                              <Input placeholder="Phone (e.g. 555-123-4567)" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="ein"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>EIN</FormLabel>
                            <FormControl>
                              <Input placeholder="XX-XXXXXXX" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="tax_classification"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Tax Classification</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select tax classification" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="LLC – Partnership">LLC – Partnership</SelectItem>
                                <SelectItem value="LLC – C Corporation">LLC – C Corporation</SelectItem>
                                <SelectItem value="LLC – S Corporation">LLC – S Corporation</SelectItem>
                                <SelectItem value="C Corporation">C Corporation</SelectItem>
                                <SelectItem value="S Corporation">S Corporation</SelectItem>
                                <SelectItem value="Individual or Single LLC">Individual or Single LLC</SelectItem>
                                <SelectItem value="Partnership">Partnership</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="space-y-4 pt-4">
                        <h3 className="text-lg font-medium">Configuration</h3>
                        
                        <FormField
                          control={form.control}
                          name="pricing_table_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Pricing Table</FormLabel>
                              <Select
                                onValueChange={(value) => field.onChange(Number(value))}
                                value={field.value?.toString()}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a pricing table" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {renderPricingTableOptions()}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="grid gap-2">
                          <Label htmlFor="document-groups">Document Groups</Label>
                          <div className="border rounded-md p-3">
                            {renderDocumentGroups()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </UnderlinedTabsContent>

                  <UnderlinedTabsContent value="billing" className="space-y-4">
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="billing_address"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Billing Address</FormLabel>
                            <FormControl>
                              <AddressAutocomplete 
                                placeholder="Search for a billing address" 
                                className="w-full"
                                onAddressSelect={handleBillingAddressSelect}
                                value={field.value || ''}
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                }}
                                name={field.name}
                                onBlur={field.onBlur}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="billing_unit"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Billing Unit</FormLabel>
                            <FormControl>
                              <Input placeholder="Billing Unit" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="billing_state"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Billing State</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select state" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {US_STATES.map((state) => (
                                    <SelectItem key={state.value} value={state.value}>
                                      {state.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="billing_city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Billing City</FormLabel>
                              <FormControl>
                                <Input placeholder="Billing City" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="billing_zip"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Billing Zip</FormLabel>
                              <FormControl>
                                <Input placeholder="Billing Zip" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="space-y-4 pt-4">
                        <h3 className="text-lg font-medium">Financial Information</h3>

                        <FormField
                          control={form.control}
                          name="account_holder_name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Account Holder Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Account Holder Name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="bank_name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Bank Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Bank Name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="account_type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Bank Account Type</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select account type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="checking">Checking</SelectItem>
                                  <SelectItem value="savings">Savings</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="routing_number"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Routing Number</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Routing Number"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="account_number"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Account Number</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Account Number"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="flex items-center space-x-2 pt-4 pb-4">
                          <Checkbox
                            id="useSameBillingAddress"
                            checked={useSameBillingAddress}
                            onCheckedChange={(checked) => setUseSameBillingAddress(checked === true)}
                          />
                          <Label htmlFor="useSameBillingAddress">
                            Use same address as billing address for payout account
                          </Label>
                        </div>

                        <h3 className="text-lg font-medium">Payout Account Address</h3>

                        <FormField
                          control={form.control}
                          name="street_address"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Street Address</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Street Address"
                                  {...field}
                                  disabled={useSameBillingAddress}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <FormField
                            control={form.control}
                            name="payout_city"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>City</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="City"
                                    {...field}
                                    disabled={useSameBillingAddress}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="state_province"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>State / Province</FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value || ""}
                                  disabled={useSameBillingAddress}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select state" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {US_STATES.map((state) => (
                                      <SelectItem key={state.value} value={state.value}>
                                        {state.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="postcode"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Postal Code</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Postal Code"
                                    {...field}
                                    disabled={useSameBillingAddress}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </UnderlinedTabsContent>

                  <UnderlinedTabsContent value="property-address" className="space-y-4">
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="default_property_address"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Address</FormLabel>
                            <FormControl>
                              <AddressAutocomplete 
                                placeholder="Search for an address" 
                                className="w-full"
                                onAddressSelect={handleAddressSelect}
                                value={field.value || ''}
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                  console.log("Address input changed:", e.target.value);
                                }}
                                name={field.name}
                                onBlur={field.onBlur}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="default_property_state"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>State</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select state" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {US_STATES.map((state) => (
                                    <SelectItem key={state.value} value={state.value}>
                                      {state.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="default_property_city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>City</FormLabel>
                              <FormControl>
                                <Input placeholder="City" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="default_property_zip"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Zip Code</FormLabel>
                              <FormControl>
                                <Input placeholder="Zip Code" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="default_property_latitude"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Latitude</FormLabel>
                              <div className="flex items-center gap-2">
                                <FormControl>
                                  <Input
                                    type="number"
                                    placeholder="Latitude"
                                    className="w-full"
                                    {...field}
                                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="default_property_longitude"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Longitude</FormLabel>
                              <div className="flex items-center gap-2">
                                <FormControl>
                                  <Input
                                    type="number"
                                    placeholder="Longitude"
                                    className="w-full"
                                    {...field}
                                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                                  />
                                </FormControl>
                                <LocationPickerButton 
                                  latitude={form.getValues('default_property_latitude')} 
                                  longitude={form.getValues('default_property_longitude')} 
                                  onLocationSelect={handleMapLocationSelect}
                                />
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </UnderlinedTabsContent>
                </UnderlinedTabs>

                <div className="flex justify-end pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="mr-2"
                    onClick={() => navigate('/backoffice/associations')}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : "Create Association"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
};

export default AssociationNew;
